import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import MaintenanceBanner from './components/shared/MaintenanceBanner';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ModalProvider } from './contexts/ModalContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute, AdminRoute, AgentRoute, GuestRoute } from './components/shared/ProtectedRoute';
import Navbar from './components/shared/Navbar';
import './styles/global.css';

import LandingPage from './pages/LandingPage';
const MaintenancePage = React.lazy(() => import('./pages/MaintenancePage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const LoginPage = React.lazy(() => import('./pages/AuthPages').then(module => ({ default: module.LoginPage })));
const ForgotPasswordPage = React.lazy(() => import('./pages/AuthPages').then(module => ({ default: module.ForgotPasswordPage })));
const ResetPasswordPage = React.lazy(() => import('./pages/AuthPages').then(module => ({ default: module.ResetPasswordPage })));
const RegisterOtpPage = React.lazy(() => import('./pages/RegisterOtpPage.jsx'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const AdminChatPage = React.lazy(() => import('./pages/AdminChatPage'));
const SupportDashboardPage = React.lazy(() => import('./pages/SupportDashboardPage'));
const AIChatPage = React.lazy(() => import('./pages/AIChatPage'));
const LiveChat = React.lazy(() => import('./components/shared/LiveChat'));
const TermsPage = React.lazy(() => import('./pages/LegalPages').then(module => ({ default: module.TermsPage })));
const PrivacyPage = React.lazy(() => import('./pages/LegalPages').then(module => ({ default: module.PrivacyPage })));
const DeprecatedAuthPage = React.lazy(() => import('./pages/AuthPages').then(module => ({ default: module.DeprecatedAuthPage })));
const NotificationsPage = React.lazy(() => import('./pages/user/NotificationsPage'));

const useDeterrents = () => {
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') return; // Allow in development for debugging

    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    const handleKeyDown = (e) => {
      // Disable F12, Ctrl+Shift+I, Ctrl+Shift+C, Ctrl+Shift+J, Ctrl+U
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'C' || e.key === 'J')) ||
        (e.metaKey && e.altKey && (e.key === 'I' || e.key === 'C' || e.key === 'J')) ||
        (e.ctrlKey && e.key === 'U') ||
        (e.metaKey && e.key === 'U')
      ) {
        e.preventDefault();
      }
    };

    window.addEventListener('contextmenu', handleContextMenu);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('contextmenu', handleContextMenu);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
};

const AuthHandler = () => {
  const navigate = useNavigate();
  const { logout, user, loading } = useAuth();
  
  useEffect(() => {
    if (loading) return; // Wait for user session to initialize

    // System status check to proactively block access
    const checkSystemStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/public/system-status`, { cache: 'no-store' });
        const data = await response.json();
        
        if (data.maintenance_mode) {
          window.dispatchEvent(new CustomEvent('app:maintenance'));
        } else if (window.location.pathname === '/maintenance') {
          navigate('/');
        }
        
        if (data.maintenance_starts_at) {
          window.dispatchEvent(new CustomEvent('app:maintenance_scheduled', { detail: data.maintenance_starts_at }));
        } else {
          window.dispatchEvent(new CustomEvent('app:maintenance_cancelled'));
        }
      } catch (err) {}
    };
    
    checkSystemStatus();
    const intervalId = setInterval(checkSystemStatus, 15000);

    const handleExpired = () => {
      logout();
      navigate('/login?expired=true');
    };
    window.addEventListener('auth:expired', handleExpired);
    
    // Listen for maintenance mode
    const handleMaintenance = () => {
      // Admins are immune
      if (user?.role === 'admin') return;

      // Automatically log out non-admins when maintenance is forced
      if (user) {
        logout();
      }

      // Allow users to stay on login page to authenticate as admin
      if (window.location.pathname !== '/login') {
        navigate('/maintenance');
      }
    };
    window.addEventListener('app:maintenance', handleMaintenance);
    
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('auth:expired', handleExpired);
      window.removeEventListener('app:maintenance', handleMaintenance);
    };
  }, [navigate, logout, user, loading]);
  return null;
};

const PageWrapper = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
    style={{ minHeight: '100vh' }}
  >
    {children}
  </motion.div>
);

function AnimatedRoutes() {
  const location = useLocation();

  // Group routes to prevent unnecessary re-mounts of shared layouts (like Dashboard/Admin sidebar)
  const getPathKey = (pathname) => {
    if (pathname.startsWith('/dashboard')) return '/dashboard';
    if (pathname.startsWith('/admin')) return '/admin';
    return pathname;
  };
  
  return (
    <AnimatePresence mode="wait">
      <React.Suspense fallback={
        <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="animate-pulse" style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--primary-accent)', opacity: 0.5 }} />
        </div>
      }>
        <Routes location={location} key={getPathKey(location.pathname)}>
          {/* Public */}
          <Route path="/" element={<PageWrapper><LandingPage /></PageWrapper>} />
          <Route path="/maintenance" element={<PageWrapper><MaintenancePage /></PageWrapper>} />
          <Route path="/pricing" element={<PageWrapper><PricingPage /></PageWrapper>} />
          <Route path="/terms" element={<PageWrapper><TermsPage /></PageWrapper>} />
          <Route path="/privacy" element={<PageWrapper><PrivacyPage /></PageWrapper>} />

          {/* Guest only */}
          <Route path="/login" element={<GuestRoute><PageWrapper><LoginPage /></PageWrapper></GuestRoute>} />
          <Route path="/register" element={<GuestRoute><PageWrapper><RegisterOtpPage /></PageWrapper></GuestRoute>} />
          <Route path="/forgot-password" element={<GuestRoute><PageWrapper><ForgotPasswordPage /></PageWrapper></GuestRoute>} />
          <Route path="/reset-password" element={<GuestRoute><PageWrapper><ResetPasswordPage /></PageWrapper></GuestRoute>} />
          
          {/* Deprecated auth routes */}
          <Route path="/verify-email" element={<PageWrapper><DeprecatedAuthPage /></PageWrapper>} />

          {/* Protected */}
          <Route path="/dashboard" element={<ProtectedRoute><PageWrapper><DashboardPage /></PageWrapper></ProtectedRoute>} />
          <Route path="/dashboard/market-insight" element={<ProtectedRoute><PageWrapper><DashboardPage /></PageWrapper></ProtectedRoute>} />
          <Route path="/dashboard/news" element={<ProtectedRoute><PageWrapper><DashboardPage /></PageWrapper></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><PageWrapper><NotificationsPage /></PageWrapper></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute><PageWrapper><AIChatPage /></PageWrapper></ProtectedRoute>} />

          {/* Admin/Agent */}
          <Route path="/agent/dashboard" element={
            <AgentRoute>
              <PageWrapper>
                <SupportDashboardPage />
              </PageWrapper>
            </AgentRoute>
          } />
          <Route path="/admin/chat" element={
            <AdminRoute>
              <PageWrapper>
                <AdminChatPage />
              </PageWrapper>
            </AdminRoute>
          } />
          <Route path="/admin/market-insight" element={<AdminRoute><PageWrapper><AdminPage /></PageWrapper></AdminRoute>} />
          <Route path="/admin/news" element={<AdminRoute><PageWrapper><AdminPage /></PageWrapper></AdminRoute>} />
          <Route path="/admin" element={<AdminRoute><PageWrapper><AdminPage /></PageWrapper></AdminRoute>} />
          <Route path="/admin/*" element={<AdminRoute><PageWrapper><AdminPage /></PageWrapper></AdminRoute>} />

          {/* 404 */}
          <Route path="*" element={
            <PageWrapper>
              <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, background: 'var(--bg-primary)' }}>
                <div style={{ fontSize: 120, fontFamily: 'var(--font-display)', color: 'var(--primary-accent)', fontWeight: 900, textShadow: '0 0 40px rgba(240, 185, 11, 0.2)' }}>404</div>
                <h2 style={{ fontSize: 24, fontWeight: 700 }}>Terminal access denied.</h2>
                <p style={{ color: 'var(--text-secondary)' }}>This route does not exist in the SignalBros ensemble.</p>
                <Link to="/" className="btn" style={{ 
                    marginTop: 24, background: 'var(--primary-accent)', color: '#000', padding: '12px 32px', borderRadius: 'var(--radius-pill)', fontWeight: 700 
                }}>Re-establish connection</Link>
              </div>
            </PageWrapper>
          } />
        </Routes>
      </React.Suspense>
    </AnimatePresence>
  );
}

function App() {
  useDeterrents();
  
  return (
    <ThemeProvider>
      <AuthProvider>
        <ModalProvider>
          <Router>
            <MaintenanceBanner />
            <AuthHandler />
            <Navbar />
            <AnimatedRoutes />

            <Toaster
              position="top-right"
              containerStyle={{ top: 80 }}
              toastOptions={{
                icon: null,
                style: {
                  background: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  fontFamily: 'var(--font-body)',
                  fontSize: '14px',
                  backdropFilter: 'blur(12px)',
                  padding: '16px 24px',
                  maxWidth: '400px'
                },
                success: { 
                  duration: 4000,
                  style: { border: '1px solid #00d9a3' }
                },
                error: { 
                  duration: 10000,
                  style: { border: '1px solid #ff4932' }
                },
              }}
              gutter={12}
            />
            <React.Suspense fallback={null}>
              <LiveChat />
            </React.Suspense>
          </Router>
        </ModalProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
